import React, { useState } from 'react';
import axios from 'axios';
import { Heart, Download, CheckCircle2, X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import '../styles/nonprofit-download.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NonprofitDownloadSection = () => {
  const [showModal, setShowModal] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  const isWorkEmail = (email) => {
    const freeEmailProviders = [
      'gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com', 
      'aol.com', 'icloud.com', 'mail.com', 'protonmail.com',
      'yandex.com', 'zoho.com', 'gmx.com', 'live.com', 'msn.com'
    ];
    const domain = email.split('@')[1]?.toLowerCase();
    return domain && !freeEmailProviders.includes(domain);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!firstName.trim() || !lastName.trim()) {
      setError('Please enter your full name');
      return;
    }
    
    if (!email || !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    
    if (!isWorkEmail(email)) {
      setError('Please use your work email address');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await axios.post(`${API}/peo-guide-download`, {
        first_name: firstName,
        last_name: lastName,
        email: email,
        source: 'main_nonprofit_download',
        guide_type: 'nonprofit_whitepaper'
      });
    } catch (error) {
      // Continue with download even if tracking fails
    }
    
    // Trigger download
    const pdfUrl = process.env.REACT_APP_NONPROFIT_GUIDE_PDF || 'https://customer-assets.emergentagent.com/job_2bac5358-3276-4771-b6d1-4470e68b545a/artifacts/f1hxm8az_How%20PEOs%20Support%20Nonprofits%20%282026%29.pdf';
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'How-PEOs-Support-Nonprofits-2026.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setIsSubmitting(false);
    setIsSuccess(true);
    
    setTimeout(() => {
      setShowModal(false);
      setIsSuccess(false);
      setFirstName('');
      setLastName('');
      setEmail('');
    }, 3000);
  };

  return (
    <>
      <section className="nonprofit-download-section">
        <div className="section-container">
          <div className="nonprofit-download-content">
            <div className="nonprofit-download-text">
              <div className="nonprofit-badge">
                <Heart size={16} />
                <span>FREE NONPROFIT RESOURCE</span>
              </div>
              <h2 className="nonprofit-download-title">
                How Professional Employer Organizations Support Nonprofit Success
              </h2>
              <p className="nonprofit-download-subtitle">
                A Strategic Guide for Nonprofit Leaders & Board Members
              </p>
              <ul className="nonprofit-download-benefits">
                <li><CheckCircle2 size={18} /> Understand how PEOs can strengthen your mission</li>
                <li><CheckCircle2 size={18} /> Learn cost-saving strategies for nonprofits</li>
                <li><CheckCircle2 size={18} /> Discover HR solutions tailored to your needs</li>
              </ul>
            </div>
            <div className="nonprofit-download-cta">
              <button 
                className="nonprofit-download-btn"
                onClick={() => setShowModal(true)}
                data-testid="nonprofit-download-btn"
              >
                <Download size={20} />
                Download Free Guide
              </button>
              <p className="nonprofit-download-note">No spam. Instant download.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Download Modal */}
      {showModal && (
        <div className="nonprofit-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="nonprofit-modal" onClick={(e) => e.stopPropagation()}>
            <button className="nonprofit-modal-close" onClick={() => setShowModal(false)}>
              <X size={24} />
            </button>

            {isSuccess ? (
              <div className="nonprofit-modal-success">
                <div className="nonprofit-success-icon">
                  <CheckCircle2 size={64} />
                </div>
                <h3>Download Starting!</h3>
                <p>Check your downloads folder for the guide.</p>
              </div>
            ) : (
              <>
                <div className="nonprofit-modal-header">
                  <div className="nonprofit-modal-icon">
                    <Heart size={40} />
                  </div>
                  <h3>Get Your Free Nonprofit PEO Guide</h3>
                  <p>How Professional Employer Organizations Support Nonprofit Success</p>
                </div>

                <form onSubmit={handleSubmit} className="nonprofit-modal-form">
                  <div className="nonprofit-form-row">
                    <div className="nonprofit-form-field">
                      <label>First Name</label>
                      <Input
                        type="text"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        placeholder="John"
                        required
                        data-testid="nonprofit-first-name"
                      />
                    </div>
                    <div className="nonprofit-form-field">
                      <label>Last Name</label>
                      <Input
                        type="text"
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        placeholder="Smith"
                        required
                        data-testid="nonprofit-last-name"
                      />
                    </div>
                  </div>
                  <div className="nonprofit-form-field">
                    <label>Work Email</label>
                    <Input
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e.target.value);
                        setError('');
                      }}
                      placeholder="john@yourorganization.org"
                      required
                      data-testid="nonprofit-email"
                    />
                  </div>
                  {error && <div className="nonprofit-form-error">{error}</div>}
                  <Button 
                    type="submit" 
                    className="nonprofit-submit-btn"
                    disabled={isSubmitting}
                    data-testid="nonprofit-submit-btn"
                  >
                    {isSubmitting ? 'Processing...' : 'Download Now'}
                  </Button>
                </form>

                <p className="nonprofit-modal-privacy">
                  We respect your privacy. No spam, ever.
                </p>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default NonprofitDownloadSection;
